import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useRouter } from "@/lib/navigation";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import styles from "./OnboardingChecklist.module.css";
import Confetti from "react-confetti";

const CHECKLIST_ITEMS = [
  {
    id: "profile",
    title: "Complete Your Profile",
    description: "Add a bio and profile picture",
    href: (username) => `/users/${username}`,
  },
  {
    id: "firstBook",
    title: "Add Your First Book",
    description: "Search and mark a book as read or want to read",
    href: "/search?tab=books",
  },
  {
    id: "createList",
    title: "Create a List",
    description: "Create your first book collection",
    href: "/search?tab=books",
  },
  {
    id: "writeReview",
    title: "Write Your First Review",
    description: "Share your thoughts about a book",
    href: "/search?tab=books",
  },
  {
    id: "followReaders",
    title: "Connect with Readers",
    description: "Follow another reader",
    href: "/search?tab=users",
  },
];

export default function OnboardingChecklist() {
  const router = useRouter();
  const {
    user,
    showOnboardingChecklist,
    isChecklistItemCompleted,
    dismissChecklist,
  } = useAuth();
  const [isExpanded, setIsExpanded] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [hasCelebrated, setHasCelebrated] = useState(false);
  const [windowDimension] = useState({
    width: typeof window !== "undefined" ? window.innerWidth : 0,
    height: typeof window !== "undefined" ? window.innerHeight : 0,
  });

  const completedCount = CHECKLIST_ITEMS.filter((item) =>
    isChecklistItemCompleted(item.id)
  ).length;

  const progress = (completedCount / CHECKLIST_ITEMS.length) * 100;

  // Single effect to handle the celebration
  useEffect(() => {
    if (progress === 100 && !hasCelebrated) {
      setHasCelebrated(true);
      setShowCelebration(true);
      setTimeout(() => {
        setShowCelebration(false);
      }, 3000);
    }
  }, [progress, hasCelebrated]);

  if (!showOnboardingChecklist) return null;

  const handleItemClick = (item) => {
    if (!isChecklistItemCompleted(item.id)) {
      router.push(item.href(user.username));
    }
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    dismissChecklist();
  };

  return (
    <div className={`${styles.container} ${isExpanded ? styles.expanded : ""}`}>
      <div className={styles.header} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.headerContent}>
          <h3>Getting Started</h3>
          <div className={styles.progressInfo}>
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className={styles.progressText}>
              {completedCount}/{CHECKLIST_ITEMS.length}
            </span>
          </div>
        </div>
        <div className={styles.headerControls}>
          <button
            className={styles.toggleButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
          >
            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </button>
          <button
            className={styles.dismissButton}
            onClick={handleDismiss}
            title="Dismiss checklist"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className={styles.itemsList}>
          {CHECKLIST_ITEMS.map((item) => {
            const completed = isChecklistItemCompleted(item.id);
            return (
              <div
                key={item.id}
                className={`${styles.item} ${
                  completed ? styles.completed : ""
                }`}
                onClick={() => handleItemClick(item)}
              >
                <div className={styles.itemContent}>
                  <div className={styles.itemIcon}>
                    {completed ? (
                      <CheckCircleIcon className={styles.completedIcon} />
                    ) : (
                      <RadioButtonUncheckedIcon
                        className={styles.incompleteIcon}
                      />
                    )}
                  </div>
                  <div className={styles.itemText}>
                    <div className={styles.itemTitle}>{item.title}</div>
                    <p className={styles.itemDescription}>{item.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCelebration && (
        <div className={styles.celebrationOverlay}>
          <Confetti
            width={windowDimension.width}
            height={windowDimension.height}
            numberOfPieces={500}
            recycle={false}
            gravity={0.2}
            initialVelocityY={20}
            colors={["#4CAF50", "#2196F3", "#FF9800", "#E91E63", "#9C27B0"]}
          />
        </div>
      )}
    </div>
  );
}
